import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';

import { DeliveryStatus } from '@revelio/data-access';
import { IconButton, Menu, MenuButton } from '@revelio/ui';

import gridStyles from '../../../shared/ui/table/status-grid-theme.module.css';
import {
  type StatusTone,
  ThemedStatusTag,
} from '../../../shared/ui/table/themed-status-tag';
import type { ReportRun } from '../../CustomDash/components/Reports/types';
import { CustomReportMenuList } from '../components/custom-report-menu-list';
import { MiddlewareReportMenuList } from '../components/mw-report-menu-list';
import type { useAllReports } from '../hooks/use-all-reports';
import styles from './reports-status-column.module.css';

// Squared status pill tone per status (Figma `138:16578`). Scheduled is
// normalised to Running before lookup (see `tagStatus`).
const tagStatusTone: Record<string, StatusTone> = {
  [DeliveryStatus.Running]: 'inProgress',
  [DeliveryStatus.Failed]: 'failed',
  [DeliveryStatus.Finished]: 'finished',
  [DeliveryStatus.FinishedWithEmpty]: 'finished',
};

export const ReportsStatusColumn = ({
  viewReportLink,
  value,
  report,
  customReportRun,
  refreshReports,
}: {
  viewReportLink: string | null;
  report: ReturnType<typeof useAllReports>['reports'][number] | null;
  customReportRun: ReportRun | null;
  reportStatus: DeliveryStatus;
  value: string;
  refreshReports: () => void;
}) => {
  const showMenu =
    [DeliveryStatus.Finished, DeliveryStatus.Failed].includes(
      value as DeliveryStatus
    ) && report;

  const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const tagStatus =
    value === DeliveryStatus.Scheduled ? DeliveryStatus.Running : value;

  return (
    <div className={styles.statusCell}>
      <ThemedStatusTag tone={tagStatusTone[tagStatus] ?? 'neutral'}>
        {tagStatus}
      </ThemedStatusTag>

      {showMenu && (
        <Menu>
          <MenuButton>
            <IconButton
              aria-label="Actions"
              className={gridStyles.rowActionButton}
              icon={
                <IconDotGrid1x3Vertical
                  size="1em"
                  className={styles.actionsIcon}
                  data-testid="reports_status_menu_button"
                />
              }
              size="sm"
              shape="squared"
              onClick={stopPropagation}
            />
          </MenuButton>
          {report?.reportType === 'CustomReport' ? (
            <CustomReportMenuList
              side="left"
              reportRun={customReportRun as ReportRun}
              onClick={stopPropagation}
              onActionSucceeded={() => {
                refreshReports();
              }}
            />
          ) : (
            <MiddlewareReportMenuList
              side="left"
              viewReportLink={viewReportLink}
              report={report}
              onClick={stopPropagation}
              onActionSucceeded={refreshReports}
            />
          )}
        </Menu>
      )}
    </div>
  );
};
